// import pacchetti
import React, { useState, useEffect } from "react";
import Select from "react-select";

// import styles
import "./Documenti.css";

// import hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";

export default function Documenti() {
  const { user } = useAuthContext();
  const { documents: mieiDoc, error } = useCollection("documenti", [
    "owner",
    "==",
    user.uid,
  ]);

  // Stato per i documenti organizzati
  const [bustePaga, setBustePaga] = useState({});
  const [cud, setCud] = useState([]);
  const [addebitiPersonali, setAddebitiPersonali] = useState([]);
  const [altri, setAltri] = useState([]);

  // Stato per le selezioni nei dropdown
  const [meseSelezionato, setMeseSelezionato] = useState(null);
  const [annoSelezionatoBuste, setAnnoSelezionatoBuste] = useState(null);
  const [annoSelezionatoCUD, setAnnoSelezionatoCUD] = useState(null);

  // Stato per i documenti filtrati
  const [bustaPagaSelezionata, setBustaPagaSelezionata] = useState(null);
  const [cudSelezionato, setCudSelezionato] = useState(null);

  // Funzione per ordinare i documenti dal più recente al meno recente
  const ordinaPerData = (docs) => {
    return [...docs].sort((a, b) => {
      // Se esiste createdAt, usa quello per ordinare
      if (a.createdAt && b.createdAt) {
        // Se createdAt è un timestamp di Firestore
        if (a.createdAt.seconds && b.createdAt.seconds) {
          return b.createdAt.seconds - a.createdAt.seconds;
        }
        // Se createdAt è una data JavaScript
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      // Altrimenti prova a usare la data nel campo competenza
      return new Date(b.competenza || 0) - new Date(a.competenza || 0);
    });
  };

  // Organizza i documenti per categoria e anno
  useEffect(() => {
    if (mieiDoc) {
      // Oggetto per le buste paga organizzate per anno
      const bustePagaPerAnno = {};
      const cudDocs = [];
      const addebitiDocs = [];
      const altriDocs = [];

      mieiDoc.forEach((doc) => {
        // Controlla il tipo di documento
        if (doc.tipoDocumento === "bustaPaga") {
          // Organizza le buste paga per anno
          const anno = doc.anno || "Anno non specificato";
          if (!bustePagaPerAnno[anno]) {
            bustePagaPerAnno[anno] = [];
          }
          bustePagaPerAnno[anno].push(doc);
        } else if (doc.tipoDocumento === "cud") {
          cudDocs.push(doc);
        } else if (doc.tipoDocumento === "addebitoPersonale") {
          addebitiDocs.push(doc);
        } else {
          altriDocs.push(doc);
        }
      });

      // Ordina i documenti in ciascuna categoria
      Object.keys(bustePagaPerAnno).forEach((anno) => {
        bustePagaPerAnno[anno] = ordinaPerData(bustePagaPerAnno[anno]);
      });

      setBustePaga(bustePagaPerAnno);
      setCud(ordinaPerData(cudDocs));
      setAddebitiPersonali(ordinaPerData(addebitiDocs));
      setAltri(ordinaPerData(altriDocs));

      // Reset delle selezioni quando cambiano i documenti
      setMeseSelezionato(null);
      setAnnoSelezionatoBuste(null);
      setAnnoSelezionatoCUD(null);
      setBustaPagaSelezionata(null);
      setCudSelezionato(null);
    }
  }, [mieiDoc]);

  // Genera le opzioni per i dropdown degli anni delle buste paga
  const getOpzioniAnniBuste = () => {
    return Object.keys(bustePaga).map((anno) => ({
      value: anno,
      label: anno,
    }));
  };

  // Genera le opzioni per i dropdown degli anni dei CUD
  const getOpzioniAnniCUD = () => {
    return [...new Set(cud.map((doc) => doc.anno))].map((anno) => ({
      value: anno,
      label: anno,
    }));
  };

  // Genera le opzioni per i dropdown dei mesi
  const getOpzioniMesi = (anno) => {
    if (!anno || !bustePaga[anno]) return [];

    return [...new Set(bustePaga[anno].map((doc) => doc.competenza))].map(
      (mese) => ({
        value: mese,
        label: mese,
      }),
    );
  };

  // Gestisce la selezione dell'anno per le buste paga
  const handleAnnoSelezionatoBuste = (opzione) => {
    setAnnoSelezionatoBuste(opzione);
    setMeseSelezionato(null);
    setBustaPagaSelezionata(null);
  };

  // Gestisce la selezione del mese per le buste paga
  const handleMeseSelezionato = (opzione) => {
    setMeseSelezionato(opzione);

    if (annoSelezionatoBuste && opzione) {
      const bustaTrovata = bustePaga[annoSelezionatoBuste.value].find(
        (doc) => doc.competenza === opzione.value,
      );
      setBustaPagaSelezionata(bustaTrovata);
    } else {
      setBustaPagaSelezionata(null);
    }
  };

  // Gestisce la selezione dell'anno per i CUD
  const handleAnnoSelezionatoCUD = (opzione) => {
    setAnnoSelezionatoCUD(opzione);

    if (opzione) {
      const cudTrovato = cud.find((doc) => doc.anno === opzione.value);
      setCudSelezionato(cudTrovato);
    } else {
      setCudSelezionato(null);
    }
  };

  if (mieiDoc && mieiDoc.length === 0) {
    return (
      <div className="documenti-container-main">
        <h2>I Miei Documenti</h2>
        <div className="documenti-sezione">
          <p>Nessun Documento disponibile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documenti-container-main">
      <h2>I Miei Documenti</h2>
      {error && <p className="error">{error}</p>}

      {/* Sezione Buste Paga */}
      <div className="documenti-sezione">
        <h3>Buste Paga</h3>
        {Object.keys(bustePaga).length === 0 ? (
          <p>Nessuna busta paga disponibile</p>
        ) : (
          <div className="filtri-container">
            <div className="filtro">
              <label>Anno:</label>
              <Select
                value={annoSelezionatoBuste}
                onChange={handleAnnoSelezionatoBuste}
                options={getOpzioniAnniBuste()}
                placeholder="Seleziona anno"
                isClearable
                isSearchable={false}
                className="select-filtro"
              />
            </div>

            {annoSelezionatoBuste && (
              <div className="filtro">
                <label>Mese:</label>
                <Select
                  value={meseSelezionato}
                  onChange={handleMeseSelezionato}
                  options={getOpzioniMesi(annoSelezionatoBuste.value)}
                  placeholder="Seleziona mese"
                  isClearable
                  isSearchable={false}
                  className="select-filtro"
                />
              </div>
            )}

            {bustaPagaSelezionata && (
              <div className="download-container">
                <a
                  href={bustaPagaSelezionata.downloadUrl}
                  className="btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Scarica Busta Paga {meseSelezionato.label}{" "}
                  {annoSelezionatoBuste.label}
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sezione CUD */}
      <div className="documenti-sezione">
        <h3>CUD</h3>
        {cud.length === 0 ? (
          <p>Nessun CUD disponibile</p>
        ) : (
          <div className="filtri-container">
            <div className="filtro">
              <label>Anno:</label>
              <Select
                value={annoSelezionatoCUD}
                onChange={handleAnnoSelezionatoCUD}
                options={getOpzioniAnniCUD()}
                placeholder="Seleziona anno"
                isClearable
                isSearchable={false}
                className="select-filtro"
              />
            </div>

            {cudSelezionato && (
              <div className="download-container">
                <a
                  href={cudSelezionato.downloadUrl}
                  className="btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Scarica CUD {annoSelezionatoCUD.label}
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sezione Addebiti Personali */}
      <div className="documenti-sezione">
        <h3>Addebiti Personali</h3>
        {addebitiPersonali.length === 0 ? (
          <p>Nessun addebito personale disponibile</p>
        ) : (
          <div className="documenti-container">
            {addebitiPersonali.map((doc, index) => (
              <div key={index} className="doc-card">
                <div>
                  <p>
                    <strong>Descrizione:</strong> {doc.identificativo}
                  </p>
                  <p>
                    <strong>Competenza:</strong> {doc.competenza} {doc.anno}
                  </p>
                  <p>
                    <strong>Importo:</strong> € {doc.importo}
                  </p>
                </div>
                <div className="download-container">
                  <a
                    href={doc.downloadUrl}
                    className="btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Scarica Documento
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sezione Altri Documenti */}
      <div className="documenti-sezione">
        <h3>Altri Documenti</h3>
        {altri.length === 0 ? (
          <p>Nessun altro documento disponibile</p>
        ) : (
          <div className="documenti-container">
            {altri.map((doc, index) => (
              <div key={index} className="doc-card">
                <div>
                  <p>
                    <strong>Identificativo:</strong> {doc.identificativo}
                  </p>
                  <p>
                    <strong>Competenza:</strong> {doc.competenza}
                  </p>
                  <p>
                    <strong>Anno:</strong> {doc.anno}
                  </p>
                </div>
                <div className="download-container">
                  <a
                    href={doc.downloadUrl}
                    className="btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Scarica Documento
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * COSE DA FARE
 * - LISTA DOCUMENTI
 * - CARTA D'IDENTITA'
 * - CODICE FISCALE
 * - RESIDENZA
 * - CODICE IBAN
 * - BUSTE PAGA CON DROPDOWN PER SELEZIONARE IL MESE
 * - CUD CON DROPDOWN PER SELEZIONARE ANNO
 *
 * ,
    ["createdAt", "desc"] per ordinare, verificare lezione 105 per capire come registrare l'ordinamento su firestore
 */
